import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {MapContainer, Marker, ScaleControl, Tooltip, useMap, ZoomControl} from "react-leaflet";
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import {Icon, LatLng} from "leaflet";
import FloorControl from "./controls/FloorControl";
import {ToggleControl} from "./controls/ToggleControl";
import ZoomListener from "./controls/ZoomListener";

const GoogleMapLoader = ({
                             isEliOfficeLocation,
                             isTestLocation,
                             isMYLocation,
                             alsEnabled = true,
                             allLocationMarkers = [],
                             updateLocations = true,
                             setAlsEnabled,
                             showAllLocations = false,
                             setShowAllLocations,
                             setUpdateLocations,
                             locationMarker = {}
                         }) => {
    const [mapLoading, setMapLoading] = useState(true);
    const [selectedFloor, setSelectedFloor] = useState(() => locationMarker?.floor ?? null);

    const GoogleMarkerIcon = (isLast) => new Icon({
        iconUrl: '/static/img/google-loc.png',
        iconSize: [54, 54],
        className: isLast ? "" : "grayscale-80"
    })
    const EmlMarkerIcon = (isLast) => new Icon({
        iconUrl: '/static/img/eml-marker.png',
        iconSize: [54, 54],
        className: isLast ? "" : "grayscale-80"
    })

    const floors = useMemo(() => isEliOfficeLocation ? [
        {name: "F1", url: "/static/img/1.pmtiles", value: "1"},
        {name: "F2", url: "/static/img/2.pmtiles", value: "2"},
        {name: "F3", url: "/static/img/3.pmtiles", value: "3"}
    ] : isTestLocation ? [
        {name: "F1", url: "/static/img/SBL.tif", value: "1"},
    ] : isMYLocation ? [
        {name: "F2", url: "/static/img/MY-F2.tif", value: "1"}
    ] : [], [isEliOfficeLocation, isTestLocation, isMYLocation]);

    useEffect(() => {
        if (isEliOfficeLocation) {
            setMapLoading(true);
            return;
        }
        setMapLoading(false);
    }, [isEliOfficeLocation, setMapLoading]);

    useEffect(() => {
        setSelectedFloor(locationMarker?.floor ?? null);
    }, [locationMarker]);

    const setLoaded = useCallback((loaded) => setMapLoading(!loaded), [setMapLoading]);

    const currentFloor = useMemo(() => {
        if (!floors.length) {
            return null;
        }

        const floorValue = updateLocations ? locationMarker?.floor : selectedFloor;
        return floors.find((floor) => floor.value === floorValue) ?? floors[0];
    }, [floors, locationMarker?.floor, selectedFloor, updateLocations]);

    const displayMarker = locationMarker?.id && (
        locationMarker?.type === "gps" ||
        locationMarker?.floor === currentFloor?.value
    );
    const mapLoaded = !mapLoading;
    return <>
        <div className="w-full h-[30px] bg-slate-200">
            {mapLoading && <div className='h-1.5 w-full bg-blue-100 overflow-hidden'>
                <div className='animate-progress w-full h-full bg-blue-300 origin-left-right'></div>
            </div>
            }
        </div>
        <div className="relative flex-1">
            <MapContainer attributionControl={false}
                          center={EARTH_CENTER_LAT_LNG}
                          zoom={3}
                          scrollWheelZoom={true}
                          zoomControl={false}
            >
                <ReactLeafletGoogleLayer apiKey='AIzaSyADlCDm6O5btPvU5PzWPbBBJfsSataS6ds' type={'satellite'}/>
                <ScaleControl position="bottomright"/>
                <ZoomControl position="topright"/>
                <ToggleControl className="mt-14 ml-2"
                               position="topleft"
                               label="Auto refresh"
                               toggleState={updateLocations}
                               setToggleState={(nextState) => {
                                   setUpdateLocations(nextState);
                                   if (nextState && showAllLocations) {
                                       setShowAllLocations(false);
                                   }
                               }}/>
                <ToggleControl position="bottomleft"
                               label="Show All"
                               toggleState={showAllLocations}
                               setToggleState={(nextState) => {
                                   setShowAllLocations(nextState);
                                   if (nextState && updateLocations) {
                                       setUpdateLocations(false);
                                   }
                               }}/>
                <ToggleControl position="bottomleft"
                               className="mb-14 ml-2"
                               label="ALS"
                               toggleState={alsEnabled}
                               setToggleState={setAlsEnabled}/>
                <ZoomListener mapLoaded={mapLoaded}/>
                <MarkerFlyToListener mapLoaded={mapLoaded}
                                     displayMarker={!showAllLocations && displayMarker}
                                     locationMarker={locationMarker}/>
                <FloorControl floors={floors}
                              setLoaded={setLoaded}
                              handleSelectFloor={setSelectedFloor}
                              disableSelection={updateLocations}
                              currentFloor={currentFloor}/>
                {showAllLocations && allLocationMarkers.map((marker) => (
                    <Marker key={`e-${marker.id}`}
                            position={[marker.lat, marker.lng]}
                            icon={marker.type === "aps" ? EmlMarkerIcon(marker.isLatest) : GoogleMarkerIcon(marker.isLatest)}
                            eventHandlers={{
                                click: (event) => {
                                    event.target.openTooltip();
                                }
                            }}>
                        <Tooltip direction="top" offset={[0, -20]} className="show-all-marker-label">
                            {marker.timestampLabel}
                        </Tooltip>
                    </Marker>
                ))}
                {!showAllLocations && displayMarker && (
                    <Marker key={`e-${locationMarker.id}`}
                            position={[locationMarker.lat, locationMarker.lng]}
                            icon={locationMarker.type === "aps" ? EmlMarkerIcon(true) : GoogleMarkerIcon(true)}>
                        <Tooltip>{locationMarker.id}</Tooltip>
                    </Marker>
                )}
            </MapContainer>
        </div>
    </>
};

export default memo(GoogleMapLoader);
const EARTH_CENTER_LAT_LNG = {lat: 54.331444157894936, lng: -108.15223571159713};

const MarkerFlyToListener = memo(({mapLoaded, displayMarker, locationMarker}) => {
    const map = useMap();

    useEffect(() => {
        if (!mapLoaded || !displayMarker || !locationMarker?.lat || !locationMarker?.lng) {
            return;
        }

        map.flyTo(new LatLng(locationMarker.lat, locationMarker.lng), map.getZoom(), {animate: false});
    }, [displayMarker, locationMarker?.lat, locationMarker?.lng, map, mapLoaded]);

    return null;
});
